import { useCallback, useMemo, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { AdElement, Animation, ElementTool, NewElementSpec } from '@adflow/types';

import { rectFromGesture } from '../elements/newElements';
import {
  combinedAxisAlignedBounds,
  normalizeRect,
  type AxisAlignedBounds,
} from '../canvas/selection';
import type { ViewportState } from '../canvas/viewport';
import type { EditorCanvasHistory } from '../store/history';
import { useCanvasGestures } from './useCanvasGestures';
import { useCanvasShortcuts } from './useCanvasShortcuts';
import { useCanvasViewport } from './useCanvasViewport';

/** Gap, in screen pixels, between a vector and its selection/hover outline. */
const VECTOR_SELECTION_PADDING_PX = 32;

export type UseEditorCanvasSessionArgs = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animation?: Animation | null;
  frame: { width: number; height: number };
  stageSize: { width: number; height: number };
  selectedElementIds: string[];
  onSelectionChange: (elementIds: string[]) => void;
  /**
   * Undo history owned by the editor store. Without it a finished drag is not recorded for undo.
   */
  history?: EditorCanvasHistory;
  /** The tool armed for the next gesture. A press on the canvas then draws instead of selecting. */
  activeTool?: ElementTool | null;
  onActiveToolChange?: (tool: ElementTool | null) => void;
  onInsertElement?: (spec: NewElementSpec) => void;
  onDeleteElements?: (elementIds: string[]) => void;
};

export type EditorCanvasSession = {
  stageRef: React.RefObject<HTMLDivElement>;
  viewport: ViewportState;
  displayArtwork: AdElement[];
  selectionBounds: AxisAlignedBounds | null;
  /** One entry per visible selected element; empty unless more than one is selected. */
  selectedItemBounds: Array<{ id: string; bounds: AxisAlignedBounds }>;
  /** Live marquee rectangle in stage coordinates, or null when no marquee is being drawn. */
  marqueeBounds: AxisAlignedBounds | null;
  hoverBounds: AxisAlignedBounds | null;
  isHoveringTarget: boolean;
  /** Tool armed for the next gesture, if any. */
  activeTool: ElementTool | null;
  /** Live rectangle of a new element being dragged out, in stage coordinates. */
  drawBounds: AxisAlignedBounds | null;
  isPanArmed: boolean;
  isPanning: boolean;
  undoDepth: number;
  redoDepth: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetViewport: () => void;
  panByScreen: (delta: { x: number; y: number }) => void;
  undo: () => void;
  redo: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
  onPointerLeave: () => void;
};

export function useEditorCanvasSession({
  elements,
  resolvedElements,
  animation = null,
  frame,
  stageSize,
  selectedElementIds,
  onSelectionChange,
  history,
  activeTool = null,
  onActiveToolChange,
  onInsertElement,
  onDeleteElements,
}: UseEditorCanvasSessionArgs): EditorCanvasSession {
  const stageRef = useRef<HTMLDivElement>(null);
  const camera = useCanvasViewport({ stageRef, frame, stageSize });
  const gestures = useCanvasGestures({
    camera,
    elements,
    resolvedElements,
    animation,
    selectedElementIds,
    onSelectionChange,
    history,
    activeTool,
    onActiveToolChange,
    onInsertElement,
  });
  useCanvasShortcuts({
    camera,
    cancelGesture: gestures.cancelGesture,
    elements,
    selectedElementIds,
    history,
    activeTool,
    onActiveToolChange,
    onInsertElement,
    onDeleteElements,
  });

  const { gesture, displayArtwork, hitTargets, hoveredElementId } = gestures;
  const zoom = camera.viewport.zoom;

  const overlays = useMemo(() => {
    const vectorIds = new Set(
      displayArtwork.filter((element) => element.type === 'VECTOR').map((element) => element.id),
    );
    // Vectors fill their whole box, so an outline drawn exactly on the bounds is
    // hidden behind (or touches) the shape. Keep a constant on-screen gap instead.
    const vectorOutlinePadding = VECTOR_SELECTION_PADDING_PX / zoom;
    const outline = (ids: readonly string[]): AxisAlignedBounds | null => {
      const bounds = combinedAxisAlignedBounds(hitTargets, ids);
      if (!bounds || ids.length === 0 || !ids.every((id) => vectorIds.has(id))) {
        return bounds;
      }
      return {
        x: bounds.x - vectorOutlinePadding,
        y: bounds.y - vectorOutlinePadding,
        width: bounds.width + vectorOutlinePadding * 2,
        height: bounds.height + vectorOutlinePadding * 2,
      };
    };

    const marqueeRect =
      gesture?.kind === 'marquee' ? normalizeRect(gesture.originStage, gesture.currentStage) : null;

    return {
      selectionBounds: outline(selectedElementIds),
      selectedItemBounds:
        selectedElementIds.length > 1
          ? hitTargets
              .filter((target) => selectedElementIds.includes(target.id))
              .map((target) => ({ id: target.id, bounds: target.bounds }))
          : [],
      hoverBounds:
        hoveredElementId && !selectedElementIds.includes(hoveredElementId)
          ? outline([hoveredElementId])
          : null,
      marqueeBounds:
        marqueeRect && (marqueeRect.width > 0 || marqueeRect.height > 0) ? marqueeRect : null,
      // A plain click has no live preview: the element simply appears at its default size.
      drawBounds:
        gesture?.kind === 'draw' && gesture.dragged
          ? rectFromGesture(gesture.tool, gesture.originStage, gesture.currentStage, {
              dragged: true,
              square: gesture.square,
            })
          : null,
    };
  }, [displayArtwork, gesture, hitTargets, hoveredElementId, selectedElementIds, zoom]);

  const undo = useCallback(() => history?.undo(), [history]);
  const redo = useCallback(() => history?.redo(), [history]);

  return {
    stageRef,
    viewport: camera.viewport,
    displayArtwork,
    ...overlays,
    isHoveringTarget: hoveredElementId != null,
    activeTool,
    isPanArmed: camera.isPanArmed,
    isPanning: camera.isPanning,
    undoDepth: history?.undoDepth ?? 0,
    redoDepth: history?.redoDepth ?? 0,
    canZoomIn: camera.canZoomIn,
    canZoomOut: camera.canZoomOut,
    setZoom: camera.setZoom,
    zoomIn: camera.zoomIn,
    zoomOut: camera.zoomOut,
    resetViewport: camera.resetViewport,
    panByScreen: camera.panByScreen,
    undo,
    redo,
    onPointerDown: gestures.onPointerDown,
    onPointerMove: gestures.onPointerMove,
    onPointerUp: gestures.onPointerUp,
    onPointerCancel: gestures.onPointerCancel,
    onPointerLeave: gestures.onPointerLeave,
  };
}
